// @ts-nocheck -- Transitional typing, matching the surrounding deal modules.
import { useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  ArrowRight, CalendarDays, Check, ChevronDown, ChevronRight, Circle, FileText, Mail, Plus,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import UserAvatar from "@/components/common/UserAvatar";
import DealSummary from "@/components/deals/DealSummary";
import DealSummaryEditor from "@/components/deals/DealSummaryEditor";
import DealStatusBanner from "@/components/deals/DealStatusBanner";
import AgentCompensationCard from "@/components/deals/AgentCompensationCard";
import { useGetAppointments } from "@/hooks/useDeals";
import { buildNeedsAttention, buildDealStatus, daysOnMarket, daysUntil } from "@/utils/deals-command.utils";
import { recentCompletedItems, resolveFinancingContingencyDate } from "@/utils/deals-view.utils";
import { resolveTaskOwnership } from "@/utils/task-ownership.utils";
import { PHASE_LABELS, formatMoney, resolveRole, roleLabel } from "@/utils/deals.utils";
import { formatAppointmentWhen } from "@/utils/appointments.utils";
import { cn, formatCurrency, formatUtcDate } from "@/utils/common.utils";
import { DealPhase, DealRole, DealType, TaskStatus } from "@/enum/common.enum";

/**
 * The Overview tab: an agent's working view of the deal (client feedback items
 * 6 and 11). It answers, in order, "what matters now, what needs action, and
 * what can wait" — the complaint was that the page read like a database record
 * dressed in cards.
 *
 * Under contract (and closed) the deal is driven by contract dates, so the
 * layout leads with the countdowns. Before that it is driven by the checklist,
 * so it leads with the next few tasks. Everything here is read from data the
 * page already loads; editing still happens in the existing dialogs and tabs.
 */
export default function DealOverview({
  deal, tasks = [], documentsOnFile = 0, canAct, onOpenTab, onOpenTask, onToggleTask, onAddTask,
  onEditProperty, onSavedTerms, onEditStatus,
}) {
  const contractView = deal.phase === DealPhase.UNDER_CONTRACT || deal.phase === DealPhase.CLOSED;
  const people = <PeopleCard deal={deal} onManage={() => onOpenTab("team")} />;
  const status = <DealStatusBanner tasks={tasks} snapshot={deal.snapshot} onEdit={onEditStatus} />;
  return contractView ? (
    <ContractOverview
      deal={deal} tasks={tasks} documentsOnFile={documentsOnFile} canAct={canAct}
      onOpenTab={onOpenTab} onOpenTask={onOpenTask} onSavedTerms={onSavedTerms} people={people} status={status}
    />
  ) : (
    <PrepareOverview
      deal={deal} tasks={tasks} canAct={canAct} onOpenTab={onOpenTab} onOpenTask={onOpenTask}
      onToggleTask={onToggleTask} onAddTask={onAddTask} onEditProperty={onEditProperty} people={people} status={status}
    />
  );
}

/* ───────────────────────── Under contract (item 6) ───────────────────────── */

function ContractOverview({ deal, tasks, documentsOnFile, canAct, onOpenTab, onOpenTask, onSavedTerms, people, status }) {
  const [editing, setEditing] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const now = useMemo(() => new Date(), []);
  const ct = deal.contract_terms || {};
  const closing = ct.closing_date || deal.close_date;
  const financing = resolveFinancingContingencyDate(deal);
  const financingIso = financing ? financing.toISOString().slice(0, 10) : null;

  const milestones = [
    { key: "dd", label: "Due diligence deadline", tile: "Due diligence", date: ct.due_diligence_end_date },
    ...(deal.financing === "cash" ? [] : [{ key: "fin", label: "Financing contingency deadline", tile: "Financing", date: financingIso }]),
    { key: "closing", label: "Closing", tile: "Closing", date: closing },
  ];
  // List-only (no countdown tile), and only once set, so they don't crowd the
  // list with "Not set" rows on deals that never track them.
  const moneyDue = [
    { key: "emd", label: "Earnest money due", date: ct.earnest_money_due_date },
    { key: "ddm", label: "Due diligence money due", date: ct.option_fee_due_date },
  ].filter((m) => m.date);
  // Upcoming only: anything before today drops off; today and unset dates stay.
  const milestoneList = [...moneyDue, ...milestones].filter((m) => (daysUntil(m.date, now) ?? 0) >= 0);

  // The one task that matters now: worst overdue first, else the next due.
  const attention = buildNeedsAttention({ deal, tasks, now });
  const nextTask = attention[0]?.task ?? buildDealStatus({ tasks, now }).next;
  const overdue = attention.filter((item) => item.kind === "overdue").length;

  const method = ct.purchase_method === "cash" || deal.financing === "cash" ? "Cash purchase" : "Financed purchase";
  const termsLine = [method, ct.earnest_money_deposit ? `${formatCurrency(ct.earnest_money_deposit)} earnest money` : null]
    .filter(Boolean).join(" · ");

  return (
    <div className="flex flex-col gap-6" data-testid="deal-overview-contract">
      {/* No title row: the deal header right above already names the address,
          stage, side and value, so repeating them here read as a duplicate. */}
      {canAct && (
        <div className="flex justify-end">
          <Button variant="dashboardPrimary" onClick={() => setEditing(true)} data-testid="overview-edit-details">
            Edit details
          </Button>
        </div>
      )}

      <div className={cn("grid overflow-hidden rounded-xl border border-dash-border bg-white", milestones.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-2")} data-testid="overview-countdowns">
        {milestones.map((m) => (
          <CountdownTile key={m.key} label={m.tile} date={m.date} now={now} highlight={m.key === "closing"} />
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]">
        <div className="flex min-w-0 flex-col gap-8">
          <section data-testid="deal-pulse-attention">
            <SectionHeader title="Next actions" aside={<OverdueNote count={overdue} />} />
            {nextTask ? (
              <NextActionCard task={nextTask} deal={deal} now={now} onOpen={canAct ? () => onOpenTask(nextTask) : undefined} />
            ) : (
              <p className="mt-3 rounded-xl border border-dash-border bg-white p-5 text-sm text-dash-muted">Nothing open right now. Every task is done.</p>
            )}
          </section>

          <section>
            <SectionHeader title="Upcoming milestones" />
            {milestoneList.length ? (
              <ul className="mt-2 divide-y divide-dash-border" data-testid="overview-milestones">
                {milestoneList.map((m) => (
                  <li key={m.key} className="flex items-center justify-between gap-4 py-3.5 text-sm">
                    <span className="font-medium text-dash-ink">{m.label}</span>
                    <span className="text-dash-muted">{m.date ? formatUtcDate(m.date) : "Not set"}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-3 text-sm text-dash-muted" data-testid="overview-milestones-empty">No upcoming milestones.</p>
            )}
          </section>

          <section className="rounded-xl border border-dash-border bg-white" data-testid="overview-contract-details">
            <button
              type="button"
              onClick={() => setTermsOpen((open) => !open)}
              aria-expanded={termsOpen}
              className="flex w-full items-center gap-4 p-5 text-left"
              data-testid="overview-terms-toggle"
            >
              <ChevronDown className={cn("size-4 shrink-0 text-dash-muted transition-transform", termsOpen && "rotate-180")} aria-hidden />
              <span className="min-w-0 flex-1">
                <span className="block font-display text-lg text-dash-ink">Contract details</span>
                <span className="block text-xs text-dash-muted">{termsLine}</span>
              </span>
              <span className="shrink-0 text-sm font-medium text-dash-greenDeep">{termsOpen ? "Hide terms" : "View all terms"}</span>
            </button>
            {termsOpen && (
              <div className="border-t border-dash-border p-3 sm:p-5">
                <DealSummary deal={deal} canEdit={canAct} onSaved={onSavedTerms} />
              </div>
            )}
          </section>

          {status}
        </div>

        <aside className="flex min-w-0 flex-col gap-4 self-start">
          <div className="rounded-xl border border-dash-border bg-white">
            {people}
            <section className="border-t border-dash-border p-5">
              <h3 className="font-display text-xl text-dash-ink">Documents</h3>
              <div className="mt-3 flex items-start gap-3">
                <FileText className="mt-0.5 size-4 shrink-0 text-dash-muted" aria-hidden />
                <div>
                  <p className="text-sm text-dash-ink" data-testid="overview-documents-count">
                    {documentsOnFile ? `${documentsOnFile} document${documentsOnFile === 1 ? "" : "s"} on file` : "No documents yet"}
                  </p>
                  <LinkButton onClick={() => onOpenTab("documents")} className="mt-1 underline underline-offset-4">Review documents</LinkButton>
                </div>
              </div>
            </section>
          </div>
          <AgentCompensationCard dealId={deal.id} />
        </aside>
      </div>

      {editing && (
        <DealSummaryEditor
          deal={deal}
          ct={ct}
          onClose={() => setEditing(false)}
          onSaved={(fresh) => { setEditing(false); onSavedTerms?.(fresh); }}
        />
      )}
    </div>
  );
}

function CountdownTile({ label, date, now, highlight }) {
  const days = daysUntil(date, now);
  const left = days === null ? "Date not set" : days < 0 ? "Passed" : days === 0 ? "Today" : `${days} day${days === 1 ? "" : "s"} left`;
  const formatted = date ? formatUtcDate(date) : "—";
  return (
    <div
      className={cn("border-dash-border p-5 [&:not(:last-child)]:border-b sm:[&:not(:last-child)]:border-b-0 sm:[&:not(:last-child)]:border-r",
        highlight && "bg-dash-greenDeep text-white")}
      data-testid={`overview-countdown-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <p className={cn("text-sm", highlight ? "text-white/80" : "text-dash-muted")}>{label}</p>
      {/* Closing reads by its date; the deadlines read by how long is left. */}
      <p className="mt-1 font-display text-3xl tracking-tight">{highlight ? formatted : left}</p>
      <p className={cn("mt-1 text-sm", highlight ? "text-white/80" : "text-dash-muted")}>{highlight ? left : formatted}</p>
    </div>
  );
}

function NextActionCard({ task, deal, now, onOpen }) {
  const days = daysUntil(task.due_date, now);
  const owner = resolveTaskOwnership(task, deal);
  const late = days !== null && days < 0;
  return (
    <div className={cn("mt-3 flex flex-col gap-4 rounded-xl border border-l-4 border-dash-border bg-white p-5 sm:flex-row sm:items-center",
      late ? "border-l-dash-rust" : "border-l-dash-amber")} data-testid="overview-next-action">
      <div className="min-w-0 flex-1">
        {task.due_date && (
          <span className={cn("inline-block rounded px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide",
            late ? "bg-dash-rust/10 text-dash-rust" : "bg-dash-amber/15 text-dash-ink")}>
            Due {formatUtcDate(task.due_date)}
          </span>
        )}
        <p className="mt-2 break-words font-display text-xl text-dash-ink">{task.title}</p>
        <p className="mt-1 text-sm text-dash-muted">Assigned to {owner.label}</p>
      </div>
      {days !== null && (
        <Pill className={cn("self-start sm:self-auto", late && "bg-dash-rust/10 text-dash-rust")}>
          {late ? `${Math.abs(days)} day${Math.abs(days) === 1 ? "" : "s"} overdue` : days === 0 ? "Due today" : `${days} day${days === 1 ? "" : "s"} left`}
        </Pill>
      )}
      {onOpen && (
        <Button variant="dashboardPrimary" onClick={onOpen} className="self-start sm:self-auto" data-testid="overview-open-task">Open task</Button>
      )}
    </div>
  );
}

/* ───────────────────── Before contract (item 11) ───────────────────── */

const PREPARE_COPY = {
  [DealPhase.PRE_LISTING]: { title: "Prepare for market", line: "keep this listing moving", checklist: "Listing preparation", hint: "Review the tasks before going live." },
  [DealPhase.LISTING]: { title: "Keep the listing moving", line: "keep this listing moving", checklist: "Listing checklist", hint: "Stay on top of the live listing." },
  [DealPhase.PRE_APPROVAL]: { title: "Get ready to shop", line: "get your buyer ready", checklist: "Pre-approval checklist", hint: "Line up financing before touring." },
  [DealPhase.HOUSE_SHOPPING]: { title: "Find the right home", line: "keep the search moving", checklist: "Home search checklist", hint: "Track tours, offers and next steps." },
};
const NUMBER_WORDS = ["No", "One", "Two", "Three"];

function PrepareOverview({ deal, tasks, canAct, onOpenTab, onOpenTask, onToggleTask, onAddTask, onEditProperty, people, status }) {
  const now = useMemo(() => new Date(), []);
  const copy = PREPARE_COPY[deal.phase] || PREPARE_COPY[DealPhase.PRE_LISTING];
  const live = tasks.filter((t) => !t.waived_at && t.is_active !== false);
  const phaseTasks = live.filter((t) => t.phase === deal.phase);
  const open = phaseTasks.filter((t) => t.status !== TaskStatus.COMPLETE);
  // Undated tasks go last, keeping checklist order among themselves.
  const upcoming = [...open]
    .sort((a, b) => (a.due_date || "9999").localeCompare(b.due_date || "9999") || (a.sort_order ?? 0) - (b.sort_order ?? 0))
    .slice(0, 3);
  const overdue = open.filter((t) => (daysUntil(t.due_date, now) ?? 0) < 0).length;
  const done = phaseTasks.length - open.length;
  const recent = recentCompletedItems(live, 2).reverse();
  const { data: appointments = [] } = useGetAppointments(deal.id);
  const nextAppointment = appointments.find((a) => a.status !== "cancelled" && new Date(a.starts_at) >= now);

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_340px]" data-testid="deal-overview-prepare">
      <div className="flex min-w-0 flex-col gap-6">
        <section data-testid="deal-pulse-attention">
          <SectionHeader
            title={copy.title}
            subtitle={upcoming.length ? `${NUMBER_WORDS[upcoming.length]} upcoming action${upcoming.length === 1 ? "" : "s"} to ${copy.line}.` : "Nothing open in this stage."}
            aside={<OverdueNote count={overdue} pill />}
          />
          <div className="mt-4 rounded-xl border border-dash-border bg-white px-5">
            <ul className="divide-y divide-dash-border">
              {upcoming.map((task) => (
                <li key={task.id} className="flex items-center gap-4 py-4" data-testid="overview-upcoming-task">
                  <button
                    type="button"
                    onClick={() => onToggleTask(task)}
                    disabled={!canAct}
                    className="shrink-0 text-dash-border transition-colors hover:text-dash-greenDeep disabled:pointer-events-none"
                    aria-label={`Mark “${task.title}” complete`}
                  >
                    <Circle className="size-6" strokeWidth={1.5} />
                  </button>
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-medium text-dash-ink">{task.title}</p>
                    <p className="text-xs text-dash-muted">{resolveTaskOwnership(task, deal).label}</p>
                  </div>
                  {task.due_date && <Pill>{formatUtcDate(task.due_date)}</Pill>}
                  {canAct && (
                    <button type="button" onClick={() => onOpenTask(task)} className="shrink-0 text-dash-muted hover:text-dash-ink" aria-label={`Open “${task.title}”`}>
                      <ChevronRight className="size-4" />
                    </button>
                  )}
                </li>
              ))}
            </ul>
            <div className={cn("flex items-center justify-between gap-3 py-4", upcoming.length && "border-t border-dash-border")}>
              <LinkButton onClick={() => onOpenTab("timeline")}>View all tasks</LinkButton>
              {canAct && <LinkButton onClick={onAddTask} data-testid="overview-add-task"><Plus className="size-4" /> Add task</LinkButton>}
            </div>
          </div>
        </section>

        <div className="grid gap-4 sm:grid-cols-2">
          <ActionCard
            icon={FileText}
            title={copy.checklist}
            body={phaseTasks.length ? `${done} of ${phaseTasks.length} tasks done. ${copy.hint}` : copy.hint}
            action="Open checklist"
            onClick={() => onOpenTab("timeline")}
          />
          <ActionCard
            icon={CalendarDays}
            title="Showings & meetings"
            body={nextAppointment ? `Next: ${nextAppointment.title || "Appointment"} · ${formatAppointmentWhen(nextAppointment)}` : "No upcoming appointments"}
            action="Schedule a meeting"
            onClick={() => onOpenTab("schedule")}
          />
        </div>

        {recent.length > 0 && (
          <section>
            <SectionHeader title="Recently completed" />
            <ul className="mt-3 divide-y divide-dash-border rounded-xl border border-dash-border bg-dash-page px-5">
              {recent.map((task) => (
                <li key={task.id} className="flex items-center gap-3 py-3.5">
                  <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-dash-greenDeep text-white"><Check className="size-3" /></span>
                  <span className="min-w-0 flex-1 truncate text-sm text-dash-ink">{task.title}</span>
                  <LinkButton onClick={() => onOpenTab("activity")}>View activity <ChevronRight className="size-4" /></LinkButton>
                </li>
              ))}
            </ul>
          </section>
        )}

        {status}
      </div>

      <aside className="flex min-w-0 flex-col gap-4 self-start">
        <div className="rounded-xl border border-dash-border bg-white">{people}</div>
        <section className="rounded-xl border border-dash-border bg-dash-hover p-5" data-testid="overview-property-details">
          <h3 className="font-display text-xl text-dash-ink">Property details</h3>
          <dl className="mt-3 flex flex-col gap-2.5 text-sm">
            <div className="flex justify-between gap-4"><dt className="text-dash-muted">Stage</dt><dd className="text-dash-ink">{PHASE_LABELS[deal.phase]}</dd></div>
            <div className="flex justify-between gap-4">
              <dt className="text-dash-muted">{deal.deal_type === DealType.SELL ? "Asking price" : "Price"}</dt>
              <dd className="text-dash-ink">{formatMoney(deal.list_price) || "—"}</dd>
            </div>
            {deal.phase === DealPhase.LISTING && daysOnMarket(deal, new Date()) !== null && (
              <div className="flex justify-between gap-4" data-testid="overview-dom">
                <dt className="text-dash-muted">Days on market</dt>
                <dd className="text-dash-ink">{daysOnMarket(deal, new Date())}</dd>
              </div>
            )}
            {deal.listing_url && (
              <div className="flex justify-between gap-4">
                <dt className="text-dash-muted">Listing</dt>
                <dd><a href={deal.listing_url} target="_blank" rel="noopener noreferrer" className="text-dash-greenDeep hover:underline" data-testid="overview-listing-link">View listing</a></dd>
              </div>
            )}
          </dl>
          {onEditProperty && (
            <button type="button" onClick={onEditProperty} className="mt-4 flex w-full items-center justify-between border-t border-dash-border pt-3 text-sm font-medium text-dash-greenDeep" data-testid="overview-edit-property">
              Edit property <ChevronRight className="size-4" />
            </button>
          )}
        </section>
        <AgentCompensationCard dealId={deal.id} />
      </aside>
    </div>
  );
}

function ActionCard({ icon: Icon, title, body, action, onClick }) {
  return (
    <section className="flex gap-4 rounded-xl border border-dash-border bg-white p-5">
      <Icon className="size-7 shrink-0 text-dash-ink" strokeWidth={1.5} aria-hidden />
      <div className="min-w-0">
        <h3 className="font-display text-lg text-dash-ink">{title}</h3>
        <p className="mt-0.5 text-sm text-dash-muted">{body}</p>
        <LinkButton onClick={onClick} className="mt-3">{action} <ArrowRight className="size-3.5" /></LinkButton>
      </div>
    </section>
  );
}

/* ───────────────────────────── Shared ───────────────────────────── */

/**
 * Clients first (from the deal itself), then everyone invited into the room.
 * The other side's names and the closing firm come from the contract terms
 * when nobody holding that seat has been invited yet.
 */
function PeopleCard({ deal, onManage }) {
  const isSell = deal.deal_type === DealType.SELL;
  const ct = deal.contract_terms || {};
  const clients = [deal.client_name, ...(deal.additional_clients || []).map((c) => c.name)].filter(Boolean);
  const rows: { key: string; label: string; name: string; sub?: string; email?: string }[] = [];
  if (clients.length) {
    rows.push({ key: "clients", label: isSell ? (clients.length > 1 ? "Sellers" : "Seller") : (clients.length > 1 ? "Buyers" : "Buyer"), name: clients.join(" & "), email: deal.client_email });
  }
  const otherSide = isSell ? ct.buyer_names : ct.seller_names;
  if (otherSide) rows.push({ key: "other", label: isSell ? "Buyer" : "Seller", name: otherSide });
  const invited = (deal.participants || []).filter((p) => p.role !== DealRole.CLIENT && p.name);
  invited.forEach((p, i) => rows.push({
    key: `p-${i}`,
    label: roleLabel(resolveRole({ role: p.role, side: p.side, hostDealType: deal.deal_type })),
    name: p.name,
    sub: [p.company, p.status && p.status !== "active" ? "Invite pending" : null].filter(Boolean).join(" · ") || undefined,
  }));
  if (ct.closing_law_firm && !invited.some((p) => p.role === DealRole.ATTORNEY)) {
    rows.push({ key: "firm", label: "Closing firm", name: ct.closing_law_firm });
  }
  if (deal.agent_name) rows.push({ key: "agent", label: "Your agent", name: deal.agent_name });

  return (
    <section className="p-5" data-testid="overview-people">
      <h3 className="font-display text-xl text-dash-ink">People</h3>
      <ul className="mt-4 flex flex-col gap-4">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center gap-3">
            <UserAvatar name={row.name} className="size-10 shrink-0" fallbackClassName="size-10 bg-dash-hover text-xs font-semibold text-dash-greenDeep" aria-hidden />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-dash-muted">{row.label}</p>
              <p className="break-words text-sm text-dash-ink">{row.name}</p>
              {row.sub && <p className="text-xs text-dash-muted">{row.sub}</p>}
            </div>
            {row.email && (
              <a href={`mailto:${row.email}`} className="shrink-0 text-dash-muted hover:text-dash-ink" aria-label={`Email ${row.name}`}>
                <Mail className="size-4" />
              </a>
            )}
          </li>
        ))}
      </ul>
      <LinkButton onClick={onManage} className="mt-4" data-testid="overview-manage-people">Manage people <ChevronRight className="size-4" /></LinkButton>
    </section>
  );
}

function SectionHeader({ title, subtitle = undefined, aside = undefined }: { title: string; subtitle?: string; aside?: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <div>
        <h2 className="font-display text-2xl tracking-tight text-dash-ink">{title}</h2>
        {subtitle && <p className="mt-0.5 text-sm text-dash-muted">{subtitle}</p>}
      </div>
      {aside}
    </div>
  );
}

function OverdueNote({ count, pill = false }) {
  const text = count ? `${count} overdue task${count === 1 ? "" : "s"}` : "No overdue tasks";
  if (pill) return <Pill className={cn("shrink-0", count && "bg-dash-rust/10 text-dash-rust")}>{text}</Pill>;
  return <span className={cn("shrink-0 pt-2 text-xs", count ? "font-medium text-dash-rust" : "text-dash-muted")}>{text}</span>;
}

function Pill({ children, className = "" }) {
  return <span className={cn("shrink-0 rounded-full bg-dash-greenDeep/10 px-2.5 py-1 text-xs font-medium text-dash-greenDeep", className)}>{children}</span>;
}

function LinkButton({ children, onClick, className = "", ...rest }) {
  return (
    <button type="button" onClick={onClick} className={cn("inline-flex items-center gap-1 text-sm font-medium text-dash-greenDeep hover:underline", className)} {...rest}>
      {children}
    </button>
  );
}
